import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { MobileAwareAutocomplete } from "@/components/search/MobileAwareAutocomplete";
import { searchStateQuery, type SearchState } from "@/components/search/SearchStateLink";
import { useAddressAutocomplete, type AddressResult } from "@/hooks/useAddressAutocomplete";
import { inferSuggestionIsCountry, pickSuggestionLabel } from "@/lib/autocomplete-label";

const listId = "mustang-location-suggestions";
const portalMarker = "mustang-location";
const locationLabel = (suggestion: AddressResult) =>
  suggestion.street ? suggestion.displayName : pickSuggestionLabel(suggestion);

export default function MustangLocationSearch({ searchState, localized = false }: { searchState: SearchState; localized?: boolean }) {
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const placeholder = localized ? t("home.search_placeholder_location_strong") : "Search by address or city";
  const autocomplete = useAddressAutocomplete();
  const [location, setLocation] = useState("");
  const [selected, setSelected] = useState<AddressResult | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const anchor = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const items = location.trim().length >= 3 ? autocomplete.suggestions : [];
  const expanded = open && (autocomplete.searching || items.length > 0);

  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || anchor.current?.contains(target)) return;
      if (target instanceof Element && target.closest(`[data-autocomplete-portal="${portalMarker}"]`)) return;
      setOpen(false);
      setActive(-1);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, []);

  const pick = (suggestion: AddressResult) => {
    setLocation(locationLabel(suggestion));
    setSelected(suggestion);
    setOpen(false);
    setActive(-1);
    autocomplete.clear();
    input.current?.focus();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const state: Record<string, unknown> = { ...searchState };
    if (location.trim()) {
      // A new place replaces stale landing geography, never the Mustang intent.
      delete state.country;
      delete state.country_code;
      delete state.location_country;
      delete state.page;
      state.location = location.trim();
      if (selected && inferSuggestionIsCountry(selected) && /^[A-Z]{2}$/.test(selected.countryCode)) {
        state.location_country = selected.countryCode;
      }
    }
    setOpen(false);
    autocomplete.clear();
    const query = searchStateQuery(state);
    navigate(query ? `/search?${query}` : "/search");
  };

  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && items.length) {
      event.preventDefault();
      setOpen(true);
      setActive((previous) => event.key === "ArrowDown"
        ? (previous + 1) % items.length
        : (previous <= 0 ? items.length - 1 : previous - 1));
    } else if (event.key === "Enter" && open && active >= 0 && items[active]) {
      event.preventDefault();
      pick(items[active]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={anchor} className="mlx-search-location">
      <form className="mlx-search" onSubmit={submit} role="search">
        <Search className="h-5 w-5" aria-hidden="true" />
        <input ref={input} type="text" name="location" value={location}
          onChange={(event) => {
            setLocation(event.target.value);
            setSelected(null);
            setActive(-1);
            setOpen(true);
            autocomplete.search(event.target.value);
          }}
          onFocus={() => { if (items.length) setOpen(true); }}
          onKeyDown={keyDown}
          placeholder={placeholder} aria-label={placeholder}
          role="combobox" aria-autocomplete="list" aria-expanded={expanded}
          aria-controls={expanded && items.length ? listId : undefined}
          aria-activedescendant={expanded && active >= 0 && items[active] ? `${listId}-option-${active}` : undefined}
          autoComplete="off" />
        {autocomplete.searching && <Loader2 className="h-4 w-4 animate-spin" role="status" aria-label={localized ? t("common.searching") : "Looking up locations"} />}
        {location && <button type="button" className="mlx-clear" aria-label={localized ? t("common.clear") : "Clear search"} onClick={() => {
          setLocation("");
          setSelected(null);
          setActive(-1);
          setOpen(false);
          autocomplete.clear();
          input.current?.focus();
        }}>&times;</button>}
        <button type="submit" className="mlx-btn">{localized ? t("common.search") : "Search"}</button>
      </form>
      <MobileAwareAutocomplete anchorRef={anchor} open={open} loading={autocomplete.searching}
        items={items} listId={listId} portalMarker={portalMarker} activeIndex={active}
        getKey={(suggestion, index) => `${suggestion.displayName}-${index}`} onSelect={pick}
        renderItem={(suggestion) => (
          <>
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{locationLabel(suggestion)}</span>
              <span className="block text-xs text-muted-foreground">{suggestion.displayName}</span>
            </span>
          </>
        )} />
      {open && !autocomplete.searching && autocomplete.query.trim().length >= 3 && !items.length && (
        <p className="mlx-search-status" role="status">{localized ? t("common.try_again") : t("landing.source_ui.no_suggestions", { lng: "en", defaultValue: "No suggestions available. You can still search for this location." })}</p>
      )}
    </div>
  );
}